import dayjs from 'dayjs'
import { t } from 'i18next'

/**
 * Formats a Date or ISO string to a localized date string.
 * Returns empty string for invalid dates.
 */
export const formatDate = (value?: Date | string): string => {
  if (!value) return ''
  const date = typeof value === 'string' ? new Date(value) : value
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
}

export const parseTimestamp = (timestamp: string): string =>
  new Date(timestamp).toLocaleDateString('en-GB')

export const formatDateLong = (
  dateString: string,
  language: string,
  includeTime = false
): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    ...(includeTime && { hour: '2-digit', minute: '2-digit' })
  }
  return new Date(dateString).toLocaleDateString(language, options)
}

/**
 * Formats a Date or ISO string to a short localized date string (e.g., "15 ene 2026").
 * Uses Spanish locale by default.
 */
export const formatDateShort = (
  date: Date | string,
  locale = 'es-ES'
): string => {
  const d = new Date(date)
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export const parseDateToISO = (date?: string) =>
  date ? dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD') : undefined

export const parseDateFromISO = (date?: string) =>
  date ? dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY') : undefined

// Parse single digit number with a leading zero
export const pad = (num: number) => String(num).padStart(2, '0')

export const getElapsedTime = (
  isoDateString: string
): { translationKey: string; translationParams?: any } => {
  const date = new Date(isoDateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    if (diffHours >= 1) {
      return {
        translationKey: 'elapsed_time.hour',
        translationParams: { count: diffHours }
      }
    } else {
      return {
        translationKey: 'elapsed_time.minute',
        translationParams: { count: diffMinutes }
      }
    }
  }

  if (diffDays === 1) {
    return {
      translationKey: 'elapsed_time.yesterday',
      translationParams: {
        time: `${pad(date.getHours())}:${pad(date.getMinutes())}`
      }
    }
  }

  if (diffDays < 7) {
    return {
      translationKey: 'elapsed_time.day',
      translationParams: { count: diffDays }
    }
  }

  if (diffDays < 365) {
    const monthKeys = [
      'months.january',
      'months.february',
      'months.march',
      'months.april',
      'months.may',
      'months.june',
      'months.july',
      'months.august',
      'months.september',
      'months.october',
      'months.november',
      'months.december'
    ]
    return {
      translationKey: 'elapsed_time.date_format',
      translationParams: {
        day: date.getDate(),
        month: t(monthKeys[date.getMonth()])
      }
    }
  }

  return {
    translationKey: `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
  }
}
